import { getActiveFrontendDeployment, type ConsoleDeployment } from '@core/runtime';

export function resolveConsoleBaseUrl(
  deployment: ConsoleDeployment = getActiveFrontendDeployment(),
): string {
  const base = String(deployment.consoleBaseUrl || deployment.relayerUrl || '').trim();
  return base.replace(/\/+$/, '');
}

export function requireConsoleBaseUrl(
  deployment: ConsoleDeployment = getActiveFrontendDeployment(),
): string {
  const base = resolveConsoleBaseUrl(deployment);
  if (!base) {
    throw new Error('Console API base URL is not configured for the selected deployment.');
  }
  return base;
}

export function buildConsoleAcceptHeaders(): HeadersInit {
  return {
    Accept: 'application/json',
  };
}

export function buildConsoleJsonHeaders(): HeadersInit {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

export async function fetchConsoleEndpoint(
  input: RequestInfo | URL,
  init: RequestInit,
  meta: { baseUrl: string; path: string; operation: string },
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error: unknown) {
    throw normalizeConsoleFetchError({
      error,
      baseUrl: meta.baseUrl,
      path: meta.path,
      operation: meta.operation,
    });
  }
}

export async function parseConsoleJson(response: Response): Promise<any> {
  return response.json().catch(() => null);
}

export function consoleErrorMessage(response: Response, body: any, fallbackPrefix: string): string {
  const apiMessage = String(body?.message || '').trim();
  return apiMessage || `${fallbackPrefix} (${response.status})`;
}

function normalizeUrl(input: string): URL | null {
  try {
    return new URL(String(input || '').trim());
  } catch {
    return null;
  }
}

function isLikelyNetworkFetchFailure(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const message = String((error as Error | undefined)?.message || '')
    .trim()
    .toLowerCase();
  if (!message) return false;
  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('load failed') ||
    message.includes('fetch failed') ||
    message.includes('err_failed')
  );
}

export function normalizeConsoleFetchError(input: {
  error: unknown;
  baseUrl: string;
  path: string;
  operation: string;
}): Error {
  const { error, baseUrl, path, operation } = input;
  if (!isLikelyNetworkFetchFailure(error)) {
    return error instanceof Error ? error : new Error(String(error));
  }

  const base = String(baseUrl || '')
    .trim()
    .replace(/\/+$/, '');
  const requestPath = String(path || '').trim();
  const endpoint =
    base && requestPath
      ? `${base}${requestPath.startsWith('/') ? requestPath : `/${requestPath}`}`
      : requestPath || base || 'unknown endpoint';
  const currentOrigin =
    typeof window !== 'undefined' ? String(window.location.origin || '').trim() : '';

  const endpointUrl = normalizeUrl(endpoint);
  const mixedContentBlocked =
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    endpointUrl?.protocol === 'http:';

  const hints: string[] = [];
  if (currentOrigin) hints.push(`dashboard origin: ${currentOrigin}`);
  if (endpointUrl?.origin) hints.push(`console origin: ${endpointUrl.origin}`);
  if (mixedContentBlocked) {
    hints.push('mixed-content blocked: HTTPS dashboard cannot call an HTTP Console API origin');
  }
  hints.push('verify relay/console server is running and CORS allows the dashboard origin');

  return new Error(
    `${operation} failed. Unable to reach Console API endpoint ${endpoint}. ${hints.join('; ')}.`,
  );
}
